import React from "react";

import { useFrame } from "../../shared/frame";
import { ROLL_MS, holdLength, nextIndex } from "./rolling";

/**
 * `idle`: one word in the line. `armed`: the incoming word is mounted at its
 * start position with transitions off. `moving`: both words are travelling.
 */
export type RollPhase = "idle" | "armed" | "moving";

export interface RollState {
    /** The phrase in flow, which sets the line's box */
    current: number;
    /** The phrase arriving over it, during a swap */
    incoming: number | null;
    phase: RollPhase;
    /** Swaps so far: the mark turns and the glow counts phrases off this */
    changes: number;
}

const INITIAL: RollState = {
    current: 0,
    incoming: null,
    phase: "idle",
    changes: 0,
};

/** Selector for the incoming word inside the line */
export const INCOMING_ATTR = "data-incoming";

/**
 * The hero's rewriting line (hero-type.js): the old word sinks and fades,
 * then the new one rises into place. The caller renders both words from the
 * returned state; this decides when, and measures the travel.
 *
 * Runs on the page's frame loop only while `enabled`. Disabling it mid-swap
 * lands the swap at once, so the line is never left with two words stalled.
 */
export const useRollingEnding = (
    phrases: readonly string[],
    lineRef: React.RefObject<HTMLElement | null>,
    enabled: boolean,
): RollState => {
    const [state, setState] = React.useState(INITIAL);
    // When the hold started (null: start it on the next frame), how long it
    // is, and when the running swap started
    const clock = React.useRef<{
        t0: number | null;
        hold: number;
        rolling: boolean;
        rollT0: number;
    }>({
        t0: null,
        hold: 0,
        rolling: false,
        rollT0: 0,
    });

    const land = React.useCallback(() => {
        const c = clock.current;
        if (!c.rolling) return;
        c.rolling = false;
        c.t0 = null;
        // The box is already at the landed word's height; drop the hold
        if (lineRef.current) lineRef.current.style.height = "";
        setState((s) =>
            s.incoming === null
                ? s
                : { ...s, current: s.incoming, incoming: null, phase: "idle" },
        );
    }, [lineRef]);

    useFrame(
        (now) => {
            const c = clock.current;
            const count = phrases.length;
            if (c.rolling) {
                if (now - c.rollT0 >= ROLL_MS) land();
                return;
            }
            if (c.t0 === null) {
                c.t0 = now;
                c.hold = holdLength(phrases[state.current % count] ?? "");
                return;
            }
            if (now - c.t0 >= c.hold) {
                c.rolling = true;
                c.rollT0 = now;
                setState((s) => ({
                    ...s,
                    incoming: nextIndex(s.current, count),
                    phase: "armed",
                    changes: s.changes + 1,
                }));
            }
        },
        enabled && phrases.length > 1,
    );

    // Stopping: resolve to one whole word, and restart the hold next time
    React.useEffect(() => {
        if (enabled) return;
        land();
        clock.current.t0 = null;
    }, [enabled, land]);

    // The incoming word is mounted at rest. Measure it, commit its start state
    // with transitions off, then hand over to the moving state.
    React.useLayoutEffect(() => {
        if (state.phase !== "armed") return;
        const line = lineRef.current;
        const incoming = line?.querySelector<HTMLElement>(`[${INCOMING_ATTR}]`);
        const outgoing = line?.querySelector<HTMLElement>(
            `:scope > :not([${INCOMING_ATTR}])`,
        );
        if (!line || !incoming || !outgoing) return;

        // The clip's padding is part of the travel, so a word that rolls out
        // clears its descenders. Fractional heights, so clearing the height
        // at the end is an exact no-op.
        const style = getComputedStyle(line);
        const pad =
            (parseFloat(style.paddingTop) || 0) +
            (parseFloat(style.paddingBottom) || 0);
        const roll = incoming.getBoundingClientRect().height + pad;
        line.style.setProperty("--roll", `${roll}px`);

        // The line takes the incoming word's height now, transitioned from an
        // explicit length (a transition out of `auto` doesn't run), so a
        // phrase that wraps grows the box rather than being clipped
        line.style.transition = "none";
        line.style.height = `${outgoing.getBoundingClientRect().height + pad}px`;
        void line.offsetHeight;
        line.style.transition = "";
        line.style.height = `${roll}px`;

        // Resolve the incoming word's start state before it moves, or both
        // ends land in one style pass and nothing animates
        void incoming.offsetHeight;
        setState((s) => ({ ...s, phase: "moving" }));
    }, [state.phase, lineRef]);

    return state;
};
